import type { Tools } from "../../types/tools";

const tools: Tools = {
  meta: {
    title: "Welcome To The Hub",
    subtitle: "Please select a tool from the sidebar.",
    zeroToolsAccess: {
      subtitle: "You do not have access to tools at the moment. Please contact your administrator to get started.",
    },
    accessDenied: {
      title: "You don't have permission to access this tool",
      blurb:
        "If you have recently been granted access, please log out and then log back in. For access requests, kindly contact your administrator.",
    },
  },
  settings: {
    title: "Settings",
    advancedAnalysisSettings: "Advanced Analysis Settings",
    anomalyManagementRp: {
      title: "Anomaly Management",
      description: "Use anomaly management where available",
      warningIfFalse:
        "Disabling Anomaly Management may result in certain assets reporting extremely high or implausible flood risk. Resulting data should be interpreted with extreme caution.",
    },
    defences: {
      title: "Defences",
      description: "Use defended layers where available",
      warningIfFalse:
        "Disabling Defences will remove all assumed flood protections. Assets will be treated as if they are fully exposed to flood related hazards, regardless of existing barriers or infrastructure.",
    },
    automatedResilience: {
      title: "{{automatedResilience}}",
      description: "Include {{automatedResilience}} runs alongside Baseline analysis",
      warningIfFalse:
        "{{automatedResilience}} results will not be generated for this analysis. You can re-run the analysis to include {{automatedResilience}} results later.",
    },
  },
  admin: {
    title: "Administration",

    hubspotDraftTester: {
      title: "HubSpot Draft Tester",
      subtitle: "Try out HubSpot pages in the Hub before publishing.",
    },
    portfolioAnalysisGrabber: {
      title: "Portfolio Analysis Grabber",
      subtitle: "Retrieve files associated with a Common Portfolio Analysis run.",
    },
  },
  comprehensive: {
    title: "Comprehensive Tools",

    blaze: {
      title: "Multi-Asset",
      subtitle: "Analyse and process large sets of assets.",
      steps: ["Provide File", "Customise", "Process Data", "View Results"],
    },
    portfolioInsights: {
      title: "Portfolio Analysis",
      subtitle:
        "The Portfolio Analysis tool delivers a comprehensive and detailed  physical climate risk assessment for portfolios of up to 20,000 single point assets.",
      description: {
        title: "What is Portfolio Analysis?",
        blurb:
          "The Portfolio Analysis tool delivers a comprehensive and detailed climate risk assessment for portfolios of up to 20,000 single point assets. This tool provides advanced asset configurability and comprehensive outputs at both an individual asset and aggregated portfolio level so you can assess a portfolio's forward-looking climate risk under a range of settings and emission scenarios. Unlike the Screening Tools, you can select the appropriate settings for each asset within your portfolio; and adjust the Height Above Ground, Build Year, Market Value, Replacement Cost, and Mortgage Rate. Detailed risk metrics are provided at 5-yearly time intervals and under all common emission scenarios. This tool is ideal for meeting the risk management, investment planning, and regulatory reporting needs of financial institutions, asset managers, and corporate sustainability teams with large-scale asset holdings.",
      },
      newRunModal: {
        upload: {
          title: "Upload A New Portfolio Template",
          blurb:
            "To begin a new analysis, upload a .csv file using the Portfolio Upload Template provided below. Download the Standard Portfolio Upload Template for basic asset customisation. For more advanced asset input settings, customise your Portfolio Upload Template using the Customise Template feature.",
        },
        review: { title: "Start New Run" },
      },
      processingAlert: {
        title: "Please do not close your browser while your analysis is processing.",
        blurb:
          "This typically takes a few minutes to a few hours, depending on the size of your data set. If processing exceeds 2 days, please reach out to the service desk.",
      },
      uploadLimitAlert: {
        user: {
          refresh:
            "An error occurred while retrieving your tool usage. Please refresh the page to try again and contact your [organisation's account owner](/settings/org/general) if the issue persists.",
          warning:
            "Your organisation is close to the limit of its annual allocation for this tool. To request additional quota, please contact your [organisation's account owner](/settings/org/general)",
          error:
            "Your organisation has reached the limit of its annual allocation for this tool. The quota will be renewed upon roll over of your subscription period. To request additional quota, please contact your [organisation's account owner](/settings/org/general)",
        },
        owner: {
          refresh:
            "An error occurred while retrieving your tool usage. Please refresh the page to try again and contact your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems) if the issue persists.",
          warning:
            "Your organisation is close to the limit of its annual allocation for this tool. To add additional quota to your account, please reach out to your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems)",
          error:
            "Your organisation has reached the limit of its annual allocation for this tool. The quota will be renewed upon roll over of your subscription period. To add additional quota to your account, please reach out to your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems)",
        },
      },
    },
    largeSite: {
      title: "Large Site Analysis",
      subtitle:
        "The Large Site Analysis tool performs rapid, high-level assessment of large or complex sites consisting of multiple grid points.",
      description: {
        title: "What is Large Site Analysis?",
        blurb:
          "The Large Site Analysis tool performs a rapid due diligence assessment of large and complex sites (up to 1 km²). The tool provides hazard-specific risk band results for all points within the site boundary and highlights the single riskiest point, enabling users to pinpoint critical vulnerabilities to support investment decisions or site selection.\n\n**Coming soon:** Geospatial visualisations of risk distribution across Large Sites will be available as an enhancement to the tool in June 2025",
      },
      startNewRun: "Start New Run",
      uploadLimitAlert: {
        user: {
          refresh:
            "An error occurred while retrieving your tool usage. Please refresh the page to try again and contact your [organisation's account owner](/settings/org/general) if the issue persists.",
          warning:
            "Your organisation is close to the limit of its annual allocation for this tool. To request additional quota, please contact your [organisation's account owner](/settings/org/general)",
          error:
            "Your organisation has reached the limit of its annual allocation for this tool. The quota will be renewed upon roll over of your subscription period. To request additional quota, please contact your [organisation's account owner](/settings/org/general)",
        },
        owner: {
          refresh:
            "An error occurred while retrieving your tool usage. Please refresh the page to try again and contact your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems) if the issue persists.",
          warning:
            "Your organisation is close to the limit of its annual allocation for this tool. To add additional quota to your account, please reach out to your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems)",
          error:
            "Your organisation has reached the limit of its annual allocation for this tool. The quota will be renewed upon roll over of your subscription period. To add additional quota to your account, please reach out to your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems)",
        },
      },
    },
    commonPortfolioAnalysis: {
      title: "Portfolio Analysis",
      subtitle: "Process a .csv file containing up to 2000 assets.",
    },
    remoteJobs: {
      title: "Remote Jobs",
      subtitle: "Process data on remote servers.",
      job: "Job",
      startNewRun: "Start New Run",
    },
  },
  assetLevelDeepDive: {
    title: "Asset-LeveL Deep Dive",
  },
  create: {
    lssFromMap: {
      title: "Analyse a Large Site",
      subtitle: "Perform rapid, high-level assessment of a large or complex site using the intuitive polygon tool.",
    },
  },
  marketIntelligence: {
    title: "Market Intelligence Tools",

    gdcr: {
      title: "Gross Domestic Climate Risk",
      subtitle: "",
    },
    mci: {
      title: "Company Database",
      subtitle: "",
    },
  },
  coordinateGeneration: {
    title: "Coordinate Generation",

    largeSiteGrid: {
      title: "Large Site Grid (Preview)",
      subtitle: "Generate coordinates from a polygon of a large site.",
    },
    shapeGridMapper: {
      title: "Shape Grid Mapper",
      subtitle: "[IN DEVELOPMENT] Generate equally spaced points from polygons and lines.",
    },
  },
  reference: {
    title: "Resources",

    database: {
      title: "Database",
      subtitle: "Browse or search our library to explore external datasets.",
    },
    tpuRiskTable: {
      title: "Hong Kong Physical Risk Table",
      subtitle: "Hong Kong Physical Risk rating and benchmarking tables using TPU aggregation",
    },
    glossary: {
      title: "$t(pages:knowledgeBase.glossary.title)",
      subtitle: "View definitions for terms used across the platform.",
    },
    archetypes: {
      title: "$t(pages:knowledgeBase.archetypes.title)",
      subtitle: "View definitions for terms used across the platform.",
    },
    methods: {
      title: "$t(pages:knowledgeBase.method.title)",
      subtitle: "View methodology documents and frequently asked questions.",
    },
    modelling: {
      title: "$t(pages:knowledgeBase.modelling.title)",
      subtitle: "View Climate ",
    },
    geocoding: {
      title: "$t(pages:knowledgeBase.geocoding.title)",
      subtitle: "View Geocoding",
    },
    riskMap: {
      title: "Risk Map (Draft)",
      subtitle: "See climate change risk by year.",
    },
    faqs: {
      title: "$t(pages:knowledgeBase.faqs.title)",
      subtitle: "View frequently asked questions",
    },
    changeLog: {
      title: "$t(pages:knowledgeBase.changeLog.title)",
      subtitle: "View changelogs",
    },
    explorer: {
      title: "Data Explorer Dashboard",
      subtitle: "View different datasets",
    },
  },
  screening: {
    title: "Screening Tools",

    uploadLimitAlert: {
      user: {
        refresh:
          "An error occurred while retrieving your tool usage. Please refresh the page to try again and contact your [organisation's account owner](/settings/org/general) if the issue persists.",
        warning:
          "Your organisation is close to the limit of its annual allocation for this tool. To request additional quota, please contact your [organisation's account owner](/settings/org/general)",
        error:
          "Your organisation has reached the limit of its annual allocation for this tool. The quota will be renewed upon roll over of your subscription period. To request additional quota, please contact your [organisation's account owner](/settings/org/general)",
      },
      owner: {
        refresh:
          "An error occurred while retrieving your tool usage. Please refresh the page to try again and contact your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems) if the issue persists.",
        warning:
          "Your organisation is close to the limit of its annual allocation for this tool. To add additional quota to your account, please reach out to your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems)",
        error:
          "Your organisation has reached the limit of its annual allocation for this tool. The quota will be renewed upon roll over of your subscription period. To add additional quota to your account, please reach out to your XDI Customer Success Manager or email [sales@xdi.systems](mailto:sales@xdi.systems)",
      },
    },
    batch: {
      title: "Quick Batch Screen",
      subtitle:
        "This tool provides a rapid assessment of climate risk for up to 30 points across six key time periods under four major emission scenarios.",
      description: {
        title: "What is Quick Batch Screen?",
        blurb:
          "The Quick Batch Screen tool provides a rapid assessment of climate risk for up to 30 points across six key time periods under four major emission scenarios. This tool provides [ABC Risk Ratings](/glossary/simple-risk-ratings) and [Significant Hazard](/glossary/significant-hazards-3) for each point on a visual map and is ideal for high-level screening and preliminary risk assessments of a limited list of locations. For the most accurate results, please enter latitude/longitude co-ordinates.\nPlease note: Quick Batch Screen applies the same asset settings to all addresses in the batch.",
      },
      steps: ["Add Data", "Process data", "Results"],
      step2Steps: ["Geocode all addresses", "Obtain risk bands for all addresses"],
    },
    single: {
      title: "Single Asset Screen",
      subtitle:
        "A flexible tool for conducting a quick and simple assessment of a single existing address or site of a potential new development. ",
      description: {
        title: "What is Single Asset Screen?",
        blurb:
          "The Single Asset Screen is a flexible tool for conducting a quick and simple assessment of a single existing address or site of a potential new development. Simply provide an address using the search capability or click anywhere on the map and select from a range of building archetypes using default settings. The Single Asset Screen will return high level climate risk results, including the asset's [risk ratings](/glossary/simple-risk-ratings) over six key time intervals and [Significant Hazard](/glossary/significant-hazards-3)  relevant to that location.",
      },
    },
  },
  portfolios: {
    portfolioLevel: {
      nav: {
        summary: "Aggregated Summary",
        assetRiskOverview: "Asset Risk",
        hazardBreakdown: "Hazard Breakdown",
      },
      summary: {
        title: "Aggregated Summary",
        description: {
          default: "A high-level summary of physical climate risk results, aggregated across all points",
          baseline:
            "A high-level summary of the portfolio's <strong>{{baseline}}</strong> physical climate risk results, aggregated across all points. The <strong>{{baseline}}</strong> risk outcome represents the physical climate risk results calculated using the asset settings provided at the time of upload.",
          enhanced_resilience:
            "A high-level summary of the portfolio's <strong>{{enhancedResilience}}</strong> physical climate risk results, aggregated across all points. The <strong>{{enhancedResilience}}</strong> risk outcome assumes a range of hypothetical adaptation measures are applied that are designed to reduce vulnerability of portfolio assets to future climate hazard.",
          compare:
            "Compare the portfolio's aggregated physical climate risk under the <strong>{{baseline}}</strong> and <strong>{{enhancedResilience}}</strong> pathways to understand the potential impact of resilience measures on overall portfolio risk.",
        },
        riskComposition: {
          bands: {
            low: "Low Risk",
            moderate: "Moderate Risk",
            high: "High Risk",
          },
        },
      },
      assetRisk: {
        title: "Key Asset Risk Metrics",
        description: {
          default:
            "The change in the key asset risk metrics over time and under multiple emission scenarios - aggregated across all points",
          baseline:
            "The change in the portfolio's <strong>{{baseline}}</strong> physical climate risk metrics over time and across multiple emissions scenarios, aggregated across all assets. The <strong>{{baseline}}</strong> risk outcome represents the physical climate risk results calculated using the asset settings provided at the time of upload.",
          enhanced_resilience:
            "The change in the portfolio's <strong>{{enhancedResilience}}</strong> physical climate risk metrics over time and across multiple emissions scenarios, aggregated across all assets. The <strong>{{enhancedResilience}}</strong> risk outcome assumes a range of hypothetical adaptation measures are applied that are designed to reduce vulnerability of portfolio assets to future climate hazard.",
          compare:
            "Compare how the portfolio's key physical climate risk metrics change over time under the <strong>{{baseline}}</strong> and <strong>{{enhancedResilience}}</strong> pathways to understand the potential impact of adaptation measures across time.",
        },
        charts: {
          mvar: {
            label: "Maximum-to-Date Value-At-Risk",
            title: "Average Maximum-to-Date Value-At-Risk %",
            description:
              "The average Maximum-to-date Value-At-Risk % (MVAR%) is a measure of the maximum potential damage, caused by climate-related hazards in any previous year up to the current selected year.",
            unavailableMessage:
              "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
          },
          ttip: {
            label: "Total Technical Insurance Premium",
            title: "Total Technical Insurance Premium",
            description:
              "Total Technical Insurance Premium (TTIP) is the sum of the cost of damage across all assets from climate change related impacts.",
            unavailableMessage: "Hazard level data not available",
          },
          productivityLoss: {
            label: "Productivity Loss",
            title: "Average Productivity Loss %",
            description:
              "The average Productivity Loss (PL) indicates the extent of projected disruption from climate related events, including periods of closure or reduced production capacity.",
          },
          failureProbability: {
            label: "Failure Probability",
            title: "Average Failure Probability %",
            description:
              "The average Failure Probability (FP) is a measure of the average annual probability of a climate event causing the assets to stop working with or without damage.",
          },
          cav: {
            label: "Climate Adjusted Value",
            title: "Climate Adjusted Value",
            description: "Change in portfolio over time across RCPs",
          },
        },
      },
      hazardBreakdown: {
        title: "Hazard Breakdown",
        description: {
          default:
            "Shows how different hazards contribute to overall risk across the entire portfolio, aggregated across all points",
          baseline:
            "Shows how individual climate hazards contribute to the portfolio's <strong>{{baseline}}</strong> physical climate risk profile. This helps identify the hazards driving the greatest risk within the portfolio under current <strong>{{baseline}}</strong> settings.",
          enhanced_resilience:
            "Shows how individual climate hazards contribute to the portfolio's overall physical climate risk profile under <strong>{{enhancedResilience}}</strong> conditions. This view highlights how hypothetical adaptation measures may reduce risk from different hazards over time.",
          compare:
            "Compare the contribution of individual hazards to overall portfolio risk under the <strong>{{baseline}}</strong> and <strong>{{enhancedResilience}}</strong> pathways to understand where adaptation has the greatest potential impact.",
        },
        unavailableMessage: "Hazard level data not available",
      },
      adaptationInsights: {
        title: "Investment Case for Resilience*",
        description:
          "Assess the financial value of potential resilience outcomes across the portfolio using avoided risk metrics derived from the difference between <strong>{{baseline}}</strong> and <strong>{{enhancedResilience}}</strong> outcomes. These insights help quantify the potential benefits of resilience investment and support early-stage decision making & financial prioritisation.",
        valueStats: {
          avoidedRisk: {
            label: "Avoided Risk Value",
            description: "",
            annual: {
              label: "Annual",
              description: "Expected reduction in damage costs for the selected year. ({{selectedYear}})",
            },
            cumulative: {
              label: "Cumulative",
              description:
                "Total expected reduction in damage costs from present day through to the selected year. ({{startYear}}-{{selectedYear}})",
            },
          },
          valueProtected: {
            label: "Value Protected",
            description: "% of asset value protected through resilience measures",
          },
          discountRate: {
            label: "Discount Rate",
            description: "",
          },
          breakevenInvestment: {
            label: "Break-Even Investment",
            description: "",
            cumulative: {
              label: "Cumulative",
              description:
                "Equivalent to the net present value of cumulative avoided risk, calculated from present day through to the selected year, based on selected discount rate",
            },
            annual: {
              label: "Annual",
              description: "Max spend in {{selectedYear}} justified by the year's avoided loss (PV)",
            },
          },
        },
        charts: {
          cumilativeRisk: {
            title: "Break-Even Investment",
            description:
              "This graph shows the maximum amount that could be invested in resilience up to a given year while maintaining a positive financial return - based on the Net Present Value of Avoided Risk using the provided discount rate",
            tooltip: {
              cumulativePeriod: "Cumulative Period",
              discountRate: "Discount Rate",
              cumulativeBreakevenInvestment: "Break-Even Investment",
            },
          },
        },
        guide: {
          title: "Indicative Values Only",
          description:
            "These figures provide a high-level estimate of the potential financial benefit of resilience investment based on the Automated Resilience conducted by the system. These indicative values do not account for actual adaptation costs, capital expenditure, operating costs or other financing considerations and should be used for early-stage screening and prioritisation insights only.",
        },
      },
      overview: {
        totalPoints: "Points",
        engineVersion: "Engine Version",
        permissions: "Permissions",
        created: "Created",
        rerun: "Last Rerun",
      },
    },
    pointLevel: {
      tabs: {
        risk: {
          note: "This view provides a high-level summary of physical climate risk across all assets in the portfolio. Each row represents an asset and its Simple Risk Rating under the selected scenario and year. Use this view to quickly identify higher-risk assets and compare risk across the portfolio. Click on a specific asset to explore its detailed results.",
        },
        exposure: {
          note: "This view shows whether each asset is currently located within a flood-prone area under a given return period. Each row represents an asset and displays a simple binary indicator for flood exposure for both riverine & surface water flooding. Use this view to quickly identify exposed assets and select an asset to explore its detailed flood risk results.",
        },
        adaptation: {
          note: `This chart helps you identify where resilience investment may have the greatest impact across assets within the portfolio. Each row represents an asset and its relative risk outcome (MVAR, TIP, PL, FP etc) under both the <strong>{{baseline}}</strong> (black) and
          <strong>{{enhancedResilience}}</strong> (teal) pathways. The difference between the two outcomes is represented by the joining line and indicates the asset's potential reduction in risk achievable through resilience measures. The greater the difference between the two risk outcomes, the more likely this asset is to benefit from resilience measures. Click on a specific asset to explore its detailed results.`,
        },
      },
      assetRisk: {
        title: "Key Asset Risk Metrics",
        description:
          "The change in an individual point's key asset risk metrics over time and under multiple emission scenarios",
        charts: {
          mvar: {
            label: "Maximum-to-Date Value-At-Risk",
            title: "Maximum-to-Date Value-At-Risk %",
            description:
              "The Maximum-to-date Value-At-Risk % (MVAR%) is a measure of the maximum potential damage, caused by climate-related hazards in any previous year up to the current selected year.",
            unavailableMessage:
              "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
          },
          productivityLoss: {
            label: "Productivity Loss",
            title: "Productivity Loss %",
            description:
              "The Productivity Loss (PL) indicates the extent of projected disruption from climate related events, including periods of closure or reduced production capacity.",
          },
          failureProbability: {
            label: "Failure Probability",
            title: "Failure Probability %",
            description:
              "The Failure Probability (FP) is a measure of the average annual probability of a climate event causing the asset to stop working - with or without damage.",
          },
          ttip: {
            label: "Technical Insurance Premium",
            title: "Technical Insurance Premium",
            description:
              "The Technical Insurance Premium (TIP) is the projected cost of damage from climate change related impacts.",
            unavailableMessage:
              "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
          },
        },
      },
      adaptation: {
        pathwayComparison: {
          title: "Resilience Summary",
          description:
            "Compare key risk metrics across resilience pathways. Use this table to quickly assess the relative impact of different asset settings and resilience pathways on risk outcomes under a selected year, hazard, and emission scenario.",
        },
        assetRisk: {
          title: "Resilience Pathway Comparison",
          description:
            "This graph shows how key risk metrics change over time under select asset configurations and resilience pathways. Compare the long-term impact of alternative asset settings and resilience measures on future risk outcomes.",
          charts: {
            mvar: {
              label: "Maximum-to-Date Value-At-Risk",
              title: "Maximum-to-Date Value-At-Risk %",
              description:
                "The Maximum-to-date Value-At-Risk % (MVAR%) is a measure of the maximum potential damage, caused by climate-related hazards in any previous year up to the current selected year.",
              unavailableMessage:
                "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
            },
            productivityLoss: {
              label: "Productivity Loss",
              title: "Productivity Loss %",
              description:
                "The Productivity Loss (PL) indicates the extent of projected disruption from climate related events, including periods of closure or reduced production capacity.",
            },
            failureProbability: {
              label: "Failure Probability",
              title: "Failure Probability %",
              description:
                "The Failure Probability (FP) is a measure of the average annual probability of a climate event causing the asset to stop working - with or without damage.",
            },
            ttip: {
              label: "Technical Insurance Premium",
              title: "Technical Insurance Premium",
              description:
                "The Technical Insurance Premium (TIP) is the projected cost of damage from climate change related impacts.",
              unavailableMessage:
                "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
            },
          },
        },
        hazardBreakdown: {
          title: "Hazard Breakdown",
          description:
            "This graph shows a detailed breakdown of risk across individual hazard types for a selected resilience pathway over time.",
          pathwaySelection: {
            title: "Select a pathway",
            description: "Select a pathway to view the breakdown of hazard risk over time",
          },
          charts: {
            mvar: {
              label: "Maximum-to-Date Value-At-Risk",
              title: "Maximum-to-Date Value-At-Risk %",
              description:
                "The Maximum-to-date Value-At-Risk % (MVAR%) is a measure of the maximum potential damage, caused by climate-related hazards in any previous year up to the current selected year.",
              unavailableMessage:
                "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
            },
            productivityLoss: {
              label: "Productivity Loss",
              title: "Productivity Loss %",
              description:
                "The Productivity Loss (PL) indicates the extent of projected disruption from climate related events, including periods of closure or reduced production capacity.",
            },
            failureProbability: {
              label: "Failure Probability",
              title: "Failure Probability %",
              description:
                "The Failure Probability (FP) is a measure of the average annual probability of a climate event causing the asset to stop working - with or without damage.",
            },
            ttip: {
              label: "Technical Insurance Premium",
              title: "Technical Insurance Premium",
              description:
                "The Technical Insurance Premium (TIP) is the projected cost of damage from climate change related impacts.",
              unavailableMessage:
                "Extreme Heat primarily affects operational disruption and equipment performance, rather than causing direct structural damage. As a result, MVAR and the downstream metrics of Simple Risk Ratings & TIP are not calculated for extreme heat. To explore risk from Extreme Heat, select Failure Probability or Productivity Loss.",
            },
          },
        },
        adaptationInsights: {
          title: "Investment Case for Resilience*",
          description:
            "Assess the financial value of potential resilience outcomes across the portfolio using avoided risk metrics. These insights help quantify the potential benefits of resilience investment and support early-stage decision making & financial prioritisation.",
          pathwaySelection: {
            title: "Select a pathway",
            description: "Select a pathway to view the Investment Case for Resilience",
          },
        },
      },
      hazardBreakdown: {
        title: "Hazard Breakdown",
        description:
          "Shows how different hazards contribute to key asset risk metrics for a individual point over time",
        unavailableMessage: "Hazard level data not available.",
      },
    },
  },
};

export default tools;
